import type { LayoutInfo } from "@/types";
import { emptyLayoutInfo } from "@/types";

/** Same key check as `storage.getLayoutInfo` — `{}` from user meta is not a layout. */
export function isCompleteLayoutInfo(info: unknown): info is LayoutInfo {
  if (!info || typeof info !== "object") return false;
  const required = Object.keys(emptyLayoutInfo());
  return required.every((k) => k in (info as object));
}

export const LAYOUT_STORAGE_KEY = "layout_info";

/**
 * Reads a saved map layout from localStorage. Returns undefined when the key
 * is missing, JSON is invalid, or `layouts` is empty — so the caller can fall
 * back to user-settings params.
 */
export function readStoredLayout(
  key: string = LAYOUT_STORAGE_KEY,
): LayoutInfo | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as LayoutInfo;
    if (!parsed?.layouts || typeof parsed.layouts !== "object")
      return undefined;
    if (Object.keys(parsed.layouts).length === 0) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/** localStorage first, then the user-settings snapshot from the backend. */
export function currentLayoutInfo(
  fromParams?: LayoutInfo,
): LayoutInfo | undefined {
  return (
    readStoredLayout() ??
    (isCompleteLayoutInfo(fromParams) ? fromParams : undefined)
  );
}

let _cachedLayoutInfo: LayoutInfo | null = null;
let _cachedLayoutRaw: string | null = null;

export const storage = {
  getLayoutInfo(): LayoutInfo {
    const infoString = localStorage.getItem("layout_info");
    if (infoString == null) {
      _cachedLayoutInfo = null;
      _cachedLayoutRaw = null;
      return emptyLayoutInfo();
    }
    if (infoString === _cachedLayoutRaw && _cachedLayoutInfo) {
      return _cachedLayoutInfo;
    }
    const layoutInfo: LayoutInfo = JSON.parse(infoString);
    const exampleLayout = emptyLayoutInfo();
    const parsedKeys = Object.keys(layoutInfo);
    const valid = Object.keys(exampleLayout).every((k) =>
      parsedKeys.includes(k),
    );
    const result = valid ? layoutInfo : exampleLayout;
    _cachedLayoutRaw = infoString;
    _cachedLayoutInfo = result;
    return result;
  },
  invalidateLayoutCache() {
    _cachedLayoutInfo = null;
    _cachedLayoutRaw = null;
  },
};
